import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { BlogPostCard } from "./components/BlogPostCard";
import { BlogHero } from "./components/BlogHero";
import { BlogSearchAndFilters } from "./components/BlogSearchAndFilters";
import { BlogSidebar } from "./components/BlogSidebar";
import { breadcrumbJsonLd, BLOG_SITE_ORIGIN } from "@/lib/blog/jsonld";
import {
  audienceFromParam,
  isPopularTopicSlug,
  postMatchesAudience,
  postMatchesPopularTopic,
  postMatchesSearch,
  type CanonicalAudience,
  type PopularTopicSlug,
} from "@/lib/blog/blogFilters";
import {
  BLOG_PAGE_SIZE,
  archivePagePath,
  computeTotalPages,
  parseArchivePage,
} from "@/lib/blog/pagination";
import { escapeInlineJson } from "@/lib/blog/escapeInlineJson";
import type { CoverImageVariants } from "@/components/blog/ResponsiveCoverImage";

/**
 * Card data shipped to the archive — minimal by design. No body_md,
 * no editorial_blocks, no cross-page rows. Reading time is precomputed
 * at build time using `src/lib/blog/readingTime.ts`.
 */
interface BootCard {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  outcome_summary: string | null;
  cover_image_url: string | null;
  cover_image_variants: CoverImageVariants | null;
  published_at: string;
  target_audience: string | null;
  topic_cluster: string | null;
  reading_minutes: number;
}

interface BlogPageBoot {
  page: number;
  totalPages: number;
  totalEligible: number;
  slice: BootCard[];
}

interface ArchiveManifest {
  generatedAt: string;
  totalEligible: number;
  pageSize: number;
  cards: BootCard[];
}

const CANONICAL = `${BLOG_SITE_ORIGIN}/blog/`;
const TITLE = "Youtemp Blog | UK Temporary Staffing Insights";
const DESCRIPTION =
  "Practical UK temporary staffing guides for agencies, employers, and temps — covering shift fulfilment, worker availability, timesheet approvals, compliance, and recruitment operations.";
const SOCIAL_IMAGE = `${BLOG_SITE_ORIGIN}/og-image-v2.png`;

const BREADCRUMBS = breadcrumbJsonLd([
  { name: "Home", url: `${BLOG_SITE_ORIGIN}/` },
  { name: "Blog", url: CANONICAL },
]);

const BOOT_SCRIPT_ID = "__blog_index_page__";
const MANIFEST_URL = "/blog/archive-data.json";

declare global {
  // eslint-disable-next-line no-var
  var __BLOG_PAGE_BOOT__: BlogPageBoot | undefined;
}

/**
 * Read the per-page boot payload that the prerender produced.
 *
 * Order:
 *  1. `globalThis.__BLOG_PAGE_BOOT__` (injected by Playwright addInitScript
 *     during prerender — the *only* source available before the inline
 *     script tag is parsed).
 *  2. `<script id="__blog_index_page__" type="application/json">…</script>`
 *     written into the static HTML — the source for real browser loads.
 *
 * Returns `null` when no boot data is available (SPA navigation in dev,
 * or a route that wasn't prerendered). Callers then fall back to the
 * manifest.
 */
function readBootData(): BlogPageBoot | null {
  if (typeof globalThis !== "undefined" && globalThis.__BLOG_PAGE_BOOT__) {
    return globalThis.__BLOG_PAGE_BOOT__;
  }
  if (typeof document === "undefined") return null;
  const el = document.getElementById(BOOT_SCRIPT_ID);
  if (!el?.textContent) return null;
  try {
    const parsed = JSON.parse(el.textContent) as BlogPageBoot;
    if (
      parsed &&
      Array.isArray(parsed.slice) &&
      typeof parsed.page === "number" &&
      typeof parsed.totalPages === "number"
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/** Slice the canonical page boundary from a full manifest. */
function sliceManifestForPage(cards: BootCard[], page: number): BootCard[] {
  const start = (page - 1) * BLOG_PAGE_SIZE;
  return cards.slice(start, start + BLOG_PAGE_SIZE);
}

export default function BlogIndexPage() {
  const params = useParams<{ page?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Lazy initializer reads prerender boot data once. Authoritative — the
  // archive page's article membership, page number and total page count
  // never change after first render unless a navigation happens.
  const [boot, setBoot] = useState<BlogPageBoot | null>(() => readBootData());
  const [manifest, setManifest] = useState<ArchiveManifest | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);

  const audience = audienceFromParam(searchParams.get("audience"));
  const topicParam = searchParams.get("topic");
  const popularTopic: PopularTopicSlug | null = isPopularTopicSlug(topicParam)
    ? topicParam
    : null;
  const query = searchParams.get("q") ?? "";
  const isFiltered =
    audience !== "all" || popularTopic !== null || query.trim().length > 0;

  // Resolve the requested page from the URL. `/blog/page/1/` is a
  // 301 alias at the Netlify layer — in the SPA we render <Navigate />
  // for the same effect on in-app navigation.
  const rawParamPage = params.page ?? null;
  // Total pages derived from boot or manifest; default to 1 (empty archive).
  const totalPages =
    boot?.totalPages ??
    (manifest ? computeTotalPages(manifest.totalEligible) : 1);
  const parsed = parseArchivePage(rawParamPage, totalPages);

  // Effective page number resolved from URL → boot → manifest.
  const currentPage =
    parsed.kind === "ok"
      ? parsed.page
      : rawParamPage === null
      ? 1
      : 1; // safe default; redirect / 404 branches handled below in JSX

  // Manifest fetch — only when needed (filters active, OR boot data is
  // missing on the SPA path). Never fetches the live Supabase view.
  useEffect(() => {
    if (manifest) return;
    if (!isFiltered && boot) return;
    let active = true;
    fetch(MANIFEST_URL, { credentials: "omit" })
      .then((res) => {
        if (!res.ok) throw new Error(`manifest http ${res.status}`);
        return res.json() as Promise<ArchiveManifest>;
      })
      .then((m) => {
        if (!active) return;
        if (!m || !Array.isArray(m.cards)) {
          setManifestError("invalid manifest");
          return;
        }
        setManifest(m);
        // If no boot data was available, synthesise one from the manifest
        // for the current page — preserves the same render path and keeps
        // the page render authoritative until a real navigation occurs.
        if (!boot) {
          const tp = computeTotalPages(m.totalEligible);
          const p = Math.min(Math.max(1, currentPage), tp);
          setBoot({
            page: p,
            totalPages: tp,
            totalEligible: m.totalEligible,
            slice: sliceManifestForPage(m.cards, p),
          });
        }
      })
      .catch((err) => {
        if (active) setManifestError(String(err?.message ?? err));
      });
    return () => {
      active = false;
    };
    // We intentionally don't include `currentPage` — once boot is filled
    // from the manifest, subsequent page navigations are full-document
    // loads that produce fresh boot data on the next page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFiltered, boot, manifest]);

  // Preview / SPA fallback: if the static manifest is unavailable (Lovable
  // dev preview, where the prerender artefacts aren't generated) AND we
  // still have no boot data, query the public `blog_posts_renderable` view
  // directly so the archive renders. Production loads never reach this
  // branch — the static manifest resolves first.
  useEffect(() => {
    if (boot) return;
    if (!manifestError) return;
    let active = true;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase
        .from("blog_posts_renderable" as never)
        .select(
          "id, slug, title, excerpt, editorial_blocks, cover_image_url, cover_image_variants, published_at, target_audience, topic_cluster, body_md",
        )
        .order("published_at", { ascending: false })
        .order("id", { ascending: false })
        .limit(100);
      if (!active || error || !Array.isArray(data)) return;
      const { estimateReadingTime } = await import("@/lib/blog/readingTime");
      const { normaliseEditorialBlocks } = await import("@/lib/blog/editorialBlocks");
      const cards: BootCard[] = (data as Array<Record<string, unknown>>).map((row) => {
        const eb = normaliseEditorialBlocks(row.editorial_blocks);
        return {
        id: String(row.id ?? ""),
        slug: String(row.slug ?? ""),
        title: String(row.title ?? ""),
        excerpt: (row.excerpt as string | null) ?? null,
        outcome_summary: eb.outcome_summary ?? null,
        cover_image_url: (row.cover_image_url as string | null) ?? null,
        cover_image_variants:
          (row.cover_image_variants as CoverImageVariants | null) ?? null,
        published_at: String(row.published_at ?? ""),
        target_audience: (row.target_audience as string | null) ?? null,
        topic_cluster: (row.topic_cluster as string | null) ?? null,
        reading_minutes: estimateReadingTime(
          (row.body_md as string | null) ?? (row.excerpt as string | null) ?? "",
        ),
        };
      });
      const tp = computeTotalPages(cards.length);
      const p = Math.min(Math.max(1, currentPage), Math.max(1, tp));
      setManifest({
        generatedAt: new Date().toISOString(),
        totalEligible: cards.length,
        pageSize: BLOG_PAGE_SIZE,
        cards,
      });
      setBoot({
        page: p,
        totalPages: Math.max(1, tp),
        totalEligible: cards.length,
        slice: sliceManifestForPage(cards, p),
      });
      // Recovery succeeded — clear the manifest-fetch error so the
      // "Unable to load posts right now." banner doesn't linger above
      // the freshly hydrated cards.
      setManifestError(null);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boot, manifestError]);

  // Filter actions: a filter always returns the user to `/blog/?…` and
  // resets page state. We use setSearchParams when already on `/blog/`,
  // and a real document navigation when on `/blog/page/N/`.
  const navigateToFilteredHome = (next: URLSearchParams) => {
    if (rawParamPage !== null && typeof window !== "undefined") {
      const qs = next.toString();
      window.location.assign(qs ? `/blog/?${qs}` : "/blog/");
      return;
    }
    setSearchParams(next, { replace: false });
  };

  const setAudience = (next: CanonicalAudience | "all") => {
    const np = new URLSearchParams(searchParams);
    if (next === "all") np.delete("audience");
    else np.set("audience", next);
    navigateToFilteredHome(np);
  };

  const setPopularTopic = (next: PopularTopicSlug | null) => {
    const np = new URLSearchParams(searchParams);
    if (next) np.set("topic", next);
    else np.delete("topic");
    navigateToFilteredHome(np);
  };

  const setQuery = (next: string) => {
    const np = new URLSearchParams(searchParams);
    const trimmed = next.trim();
    if (trimmed) np.set("q", next);
    else np.delete("q");
    if (rawParamPage !== null && typeof window !== "undefined") {
      const qs = np.toString();
      window.location.assign(qs ? `/blog/?${qs}` : "/blog/");
      return;
    }
    setSearchParams(np, { replace: true });
  };

  const clearFilters = () => {
    const np = new URLSearchParams(searchParams);
    np.delete("audience");
    np.delete("topic");
    np.delete("q");
    navigateToFilteredHome(np);
  };

  const selectTopicSlug = (slug: string) => {
    if (isPopularTopicSlug(slug)) {
      setPopularTopic(popularTopic === slug ? null : slug);
    }
  };

  // Filtered cards — drawn from the manifest, NOT the live Supabase view.
  const filteredCards = useMemo<BootCard[] | null>(() => {
    if (!isFiltered) return null;
    const source = manifest?.cards;
    if (!source) return null;
    return source.filter(
      (p) =>
        postMatchesAudience(p, audience) &&
        postMatchesPopularTopic(p, popularTopic) &&
        postMatchesSearch(p, query),
    );
  }, [isFiltered, manifest, audience, popularTopic, query]);

  // Cards to render in the grid. Filtered: all matches. Unfiltered: this
  // page's slice from boot (authoritative — never re-sliced from a fresher
  // dataset).
  const cards: BootCard[] | null = isFiltered
    ? filteredCards
    : boot?.slice ?? null;

  // Stable metadata per archive page.
  const canonicalHref = isFiltered
    ? CANONICAL
    : `${BLOG_SITE_ORIGIN}${archivePagePath(currentPage)}`;
  const robotsContent = isFiltered ? "noindex, follow" : "index, follow";
  const pageTitle =
    currentPage > 1
      ? `Youtemp Blog – Page ${currentPage} | UK Temporary Staffing Insights`
      : TITLE;
  const prevHref = currentPage > 1 ? archivePagePath(currentPage - 1) : null;
  const nextHref =
    currentPage < totalPages ? archivePagePath(currentPage + 1) : null;

  // Featured guide on the page-1 hero — first card from the page-1 slice.
  const featured = useMemo(() => {
    if (currentPage !== 1 || !boot || boot.slice.length === 0) return null;
    const p = boot.slice[0];
    return {
      slug: p.slug,
      title: p.title,
      coverImageUrl: p.cover_image_url,
      coverImageVariants: p.cover_image_variants,
      readingMinutes: p.reading_minutes,
    };
  }, [currentPage, boot]);

  // Blog JSON-LD — page 1 only, sliced from boot data.
  const blogJsonLd = useMemo(() => {
    if (currentPage !== 1 || !boot || boot.slice.length === 0) return null;
    const items = boot.slice.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${BLOG_SITE_ORIGIN}/blog/${p.slug}/`,
      datePublished: p.published_at,
      ...(p.excerpt ? { description: p.excerpt } : {}),
      ...(p.cover_image_url ? { image: p.cover_image_url } : {}),
    }));
    return {
      "@context": "https://schema.org",
      "@type": "Blog",
      "@id": `${CANONICAL}#blog`,
      name: "Youtemp Blog",
      url: CANONICAL,
      description: DESCRIPTION,
      blogPost: items,
    };
  }, [currentPage, boot]);

  // SPA-side aliases / invalid pages. Real document hits are handled by
  // the Netlify `_redirects` rule (page 1) and the static 404 fallback
  // (everything else that has no generated file).
  if (parsed.kind === "page-one-alias") {
    return <Navigate to="/blog/" replace />;
  }
  if (parsed.kind === "invalid" && rawParamPage !== null) {
    // Render a minimal 404-style block — keeps the public shell, lets the
    // user navigate back to `/blog/`. Status code on real navigations is
    // the genuine Netlify 404 (no static file exists for the path).
    return (
      <section className="container content-wide section-py">
        <Helmet>
          <title>Page not found | Youtemp Blog</title>
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={CANONICAL} />
        </Helmet>
        <h1 className="text-h2 mb-4">Archive page not found</h1>
        <p className="text-body text-muted-foreground mb-6">
          We couldn't find that archive page.
        </p>
        <a
          href="/blog/"
          className="inline-flex items-center h-10 px-4 rounded-lg border border-border bg-card text-body-sm font-medium text-foreground hover:bg-muted transition-colors"
        >
          Back to the blog
        </a>
      </section>
    );
  }

  const totalEligible = boot?.totalEligible ?? manifest?.totalEligible ?? 0;
  const avgReadingTime = (() => {
    const src = boot?.slice ?? manifest?.cards ?? [];
    if (src.length === 0) return 5;
    const total = src.reduce((acc, p) => acc + (p.reading_minutes || 0), 0);
    return Math.max(1, Math.round(total / src.length));
  })();

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta name="robots" content={robotsContent} />
        <link rel="canonical" href={canonicalHref} />
        {prevHref && <link rel="prev" href={`${BLOG_SITE_ORIGIN}${prevHref}`} />}
        {nextHref && <link rel="next" href={`${BLOG_SITE_ORIGIN}${nextHref}`} />}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={canonicalHref} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SOCIAL_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={SOCIAL_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(BREADCRUMBS)}</script>
        {blogJsonLd && (
          <script type="application/ld+json">{JSON.stringify(blogJsonLd)}</script>
        )}
      </Helmet>

      {boot && (
        <script
          id={BOOT_SCRIPT_ID}
          type="application/json"
          // Inline the per-page boot payload so the prerender captures it
          // and the hydrating browser reads it before fetching the manifest.
          dangerouslySetInnerHTML={{ __html: escapeInlineJson(boot) }}
        />
      )}

      <BlogHero
        postCount={totalEligible}
        avgReadingTime={avgReadingTime}
        featured={featured}
      />

      <section aria-label="Search articles" className="container content-wide section-py-sm">
        <BlogSearchAndFilters
          query={query}
          onQueryChange={setQuery}
          audience={audience}
          onAudienceChange={setAudience}
          popularTopic={popularTopic}
          onPopularTopicChange={setPopularTopic}
        />
      </section>

      <section id="articles" className="container content-wide section-py">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-10">
          <div className="min-w-0">
            <div className="mb-6 flex items-end justify-between gap-4">
              <h2 className="text-h2">
                Latest articles
              </h2>
            {filteredCards && (
                <p className="text-body-sm text-muted-foreground hidden sm:block">
                {filteredCards.length} {filteredCards.length === 1 ? "article" : "articles"}
                </p>
              )}
            </div>

            {manifestError && !cards && (
              <p className="text-destructive">Unable to load posts right now.</p>
            )}

            {!isFiltered && boot && boot.slice.length === 0 && (
              <div className="rounded-2xl border border-border bg-card p-10 text-center">
                <p className="text-muted-foreground">
                  No posts published yet — check back soon.
                </p>
              </div>
            )}

            {filteredCards && filteredCards.length === 0 && (
              <div className="rounded-2xl border border-border bg-card p-10 text-center">
                <p className="text-muted-foreground mb-4">
                  No articles match your search. Try a different topic.
                </p>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center h-10 px-4 rounded-lg border border-border bg-card text-body-sm font-medium text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Clear filters
                </button>
              </div>
            )}

            {cards && cards.length > 0 && (
              <>
                <div className="grid grid-gap-cards sm:grid-cols-2">
                  {cards.map((p) => (
                      <BlogPostCard
                        key={p.slug}
                        slug={p.slug}
                        title={p.title}
                        excerpt={p.excerpt}
                        coverImageUrl={p.cover_image_url}
                        coverImageVariants={p.cover_image_variants}
                        publishedAt={p.published_at}
                        bodyMd={null}
                        outcomeSummary={p.outcome_summary}
                        targetAudience={p.target_audience}
                        readingMinutes={p.reading_minutes}
                      />
                  ))}
                  {/* Balance the grid when there are very few posts so the
                      footer doesn't appear to float in dead space. */}
                  {cards.length < 3 && totalPages === 1 && (
                    <div className="rounded-2xl border border-dashed border-border bg-card/40 p-8 flex flex-col items-center justify-center text-center">
                      <p className="text-sm font-semibold text-foreground mb-1">
                        More guides coming soon
                      </p>
                      <p className="text-xs text-muted-foreground">
                        We publish new UK temp staffing insights every few weeks.
                      </p>
                    </div>
                  )}
                </div>

                {!isFiltered && totalPages > 1 && (
                  <nav
                    aria-label="Blog pagination"
                    className="mt-10 flex items-center justify-between gap-4"
                  >
                    {prevHref ? (
                      <a
                        href={prevHref}
                        rel="prev"
                        className="inline-flex items-center h-10 px-4 rounded-lg border border-border bg-card text-body-sm font-medium text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        ← Previous
                      </a>
                    ) : (
                      <span aria-hidden="true" />
                    )}
                    <p className="text-body-sm text-muted-foreground" aria-live="polite">
                      Page {currentPage} of {totalPages}
                    </p>
                    {nextHref ? (
                      <a
                        href={nextHref}
                        rel="next"
                        className="inline-flex items-center h-10 px-4 rounded-lg border border-border bg-card text-body-sm font-medium text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        Next →
                      </a>
                    ) : (
                      <span aria-hidden="true" />
                    )}
                  </nav>
                )}
              </>
            )}
          </div>

          <BlogSidebar
            featured={featured}
            onTopicSelect={selectTopicSlug}
            activeTopic={popularTopic}
          />
        </div>
      </section>
    </>
  );
}